import { useContext, useEffect } from 'react';

import { themeGet } from '@styled-system/theme-get';
import { AuthContext } from 'contexts/auth';
import { sendGTMEvent } from 'helpers/google-tag-manager';
import { bool, func, shape } from 'prop-types';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { customMutators } from 'utils/old-ui-commons/form';
import InputLabelError from 'utils/old-ui-commons/input-label-error';
import SelectLabelError from 'utils/old-ui-commons/select-label-error';

import { Box, Flex, Text } from '@olist/united';

import { AlertContext } from '../../../contexts/alert-context';
import Api from '../../../service';
import validatorForm, { CNPJ_MASK } from './validator';

const BoxLabelWrapper = styled(Box)`
  input {
    background-color: white !important;
    border: none;
    box-shadow: none;
    font-size: 19px;
    font-weight: 500;
    padding: ${themeGet('space.0')}px;
  }
`;

const InputNumberWrapper = styled(Box).attrs({
  width: 1,
  maxWidth: '352px',
  minHeight: 7,
  mb: 4,
})``;

const InputWrapper = (props) => (
  <InputNumberWrapper {...props}>
    <InputLabelError {...props} />
  </InputNumberWrapper>
);

const InputLabelWrapper = (props) => (
  <BoxLabelWrapper width={1} maxWidth="352px" {...props} minHeight={7}>
    <InputLabelError {...props} />
  </BoxLabelWrapper>
);

const CompanyInformationForm = ({
  goToNextStep,
  initialValues,
  isGtmLoaded,
  setSubmitCallback,
  updatePersonalData,
}) => {
  const { t } = useTranslation();

  const { setAlert } = useContext(AlertContext);
  const { userInfo } = useContext(AuthContext);

  useEffect(() => {
    if (userInfo?.email && isGtmLoaded) {
      sendGTMEvent('page_view', { user_id: userInfo.email });
    }
  }, [userInfo?.email, isGtmLoaded]);

  const onSubmitHandler = async (postValues) => {
    const formValues = {
      ...postValues,
      crt: postValues.crt.value || postValues.crt,
    };

    try {
      const responseValues = await Api.postCompanyInformation(formValues);
      if (responseValues) {
        updatePersonalData(responseValues);
        sendGTMEvent('etapa_cadastro', {
          etapa: 'dados_empresa',
        });
        goToNextStep();
      }
    } catch (error) {
      setAlert({
        text: t`An error occurred and the information could not be sent. Try again.`,
        show: true,
        iconName: 'alert-triangle-outline',
      });
    }
  };

  const inputCRTOptions = [
    { label: 'Simples Nacional', value: '1' },
    {
      label: 'Simples Nacional - excesso de sublimite de receita bruta',
      value: '2',
    },
    { label: 'Regime Normal', value: '3' },
  ];

  const filterCRTAccordingValues = inputCRTOptions.find((item) => item.value === initialValues.crt);

  const getValidInitialCRTValue = () => (initialValues.crt ? filterCRTAccordingValues : '');

  const setNumberValue = (args, state, utils) => {
    const input = args[0]?.target?.value;
    const finalValue = input.replace(/[^0-9]+/g, '');
    utils.changeValue(state, 'ie', () => finalValue);
  };

  return (
    <Form
      mutators={{
        setValue: customMutators.setValue,
        setNumberValue,
      }}
      onSubmit={onSubmitHandler}
      initialValues={initialValues}
      validate={validatorForm}
      render={({ form, handleSubmit, hasValidationErrors, submitting }) => {
        setSubmitCallback(handleSubmit, !hasValidationErrors, submitting);
        return (
          <form onSubmit={handleSubmit}>
            <InputLabelWrapper disabled name="cnpj" label={t`company ID`} mask={CNPJ_MASK} />
            <Text fontSize={[0, 1, 2]} mb={4} color="foundation.shadeDark">
              {t`the company data should pertence to this CNPJ`}
            </Text>
            <Flex flexDirection={['column', 'column', 'column', 'column', 'row']}>
              <InputWrapper
                name="company_name"
                label={t`company name`}
                placeholder={t`e.g. ZestByte Technology`}
                mr={3}
              />
              <InputWrapper name="brand" label={t`brand name`} placeholder={t`e.g. ZestByte`} />
            </Flex>
            <Flex flexDirection={['column', 'column', 'column', 'column', 'row']}>
              <InputWrapper
                name="ie"
                optionalLabel={t`optional`}
                onChange={form.mutators.setNumberValue}
                label={t`taxpayer ID`}
                placeholder="xxxxxxxxxxxxxx"
                mr={3}
              />
              <Box width={1} maxWidth="350px" minHeight={7} data-testid="crt-id">
                <SelectLabelError
                  isSearchable={false}
                  name="crt"
                  label={t`tax regime`}
                  placeholder={t`select`}
                  initialValue={getValidInitialCRTValue()}
                  options={inputCRTOptions}
                />
              </Box>
            </Flex>
          </form>
        );
      }}
    />
  );
};

CompanyInformationForm.propTypes = {
  goToNextStep: func.isRequired,
  updatePersonalData: func.isRequired,
  initialValues: shape({}),
  setSubmitCallback: func.isRequired,
  isGtmLoaded: bool.isRequired,
};

CompanyInformationForm.defaultProps = {
  initialValues: {},
};

export default CompanyInformationForm;
